import React from 'react';
import { Reconciliation } from '../types';

/**
 * Stanje podataka, uvek vidljivo.
 *
 * Ako se saldo konektora ne poklapa sa ERP-om, operater to mora videti pre nego
 * što išta isplanira — pogrešna lista je gora od nikakve.
 */
export default function DataHealth({ report }: { report: Reconciliation | null }) {
  if (!report) {
    return <div className="health health--unknown">Provera podataka nije učitana.</div>;
  }

  /**
   * Godišnja baza koju niko nije prebacio.
   *
   * Stoji IZNAD svega ostalog i pojavljuje se i kad se saldo poklapa — jer se
   * saldo i poklapa: prošlogodišnja baza je sama sa sobom saglasna. Ono što
   * nedostaje nije razlika u broju nego prestanak brojeva, a to bez ove rečenice
   * izgleda kao miran dan.
   */
  const godina = report.erpYear;

  const staraGodina = godina?.isStale ? (
    <div className="health health--bad">
      <strong>
        ERP je otvorio {godina.latestYear}. godinu, a aplikacija čita {godina.database}.
      </strong>{' '}
      Sve zavedeno u novoj godini je ovde nevidljivo — otpremnice, nalozi i prenosi.
      Spiskovi zbog toga ne rastu, a to liči na slab promet. Treba promeniti{' '}
      <code>ConnectionStrings__ErpDatabase</code> (i <code>ErpWriteDatabase</code>) u{' '}
      <code>web.config</code> na serveru i restartovati aplikaciju. Trenutak prebacivanja
      bira čovek: stara godina se zatvara postepeno, pa se prvo proveri da li je promet prešao.
    </div>
  ) : null;

  /**
   * Isporuke koje su predate, a ERP o njima ne zna.
   *
   * Stoji uz godisnju bazu jer odgovara na isto pitanje — sta ERP ne zna a mi
   * znamo. Prolaz koji oznacava isporuke gleda 30 dana unazad, pa otpremnica koja
   * toliko ne prodje ispadne iz svakog pokusaja i nikad se ne javi; ovo je jedino
   * mesto na kom se to vidi.
   *
   * Prazan spisak ne prikazuje nista: traka koja svaki dan kaze „nula" prestane
   * da se cita.
   */
  const backlog = report.markBacklog;

  const zaglavljene = backlog && backlog.count > 0 ? (
    <div className="health health--bad">
      <strong>
        {backlog.count === 1
          ? 'Jedna isporuka nije upisana u ERP'
          : `${backlog.count} isporuka nije upisano u ERP`}
        , i vise se ne pokusava.
      </strong>{' '}
      {backlog.notInDatabase > 0 && (
        <>
          Od toga {backlog.notInDatabase} nema u povezanoj bazi
          {godina?.database ? ` (${godina.database})` : ''} — to je otpremnica iz
          prethodne poslovne godine i oznacava se rukom, u toj bazi.{' '}
        </>
      )}
      Najstarija ceka {backlog.oldestDays}{' '}
      {backlog.oldestDays === 1 ? 'dan' : 'dana'}. Dok kolona stoji na nuli, roba se u
      ERP-u vodi kao da jos ceka prevoz.
      <ul className="health__list">
        {backlog.documents.map((d) => (
          <li key={d.documentKey}>
            <code>{d.documentKey}</code> — predato{' '}
            {new Date(d.deliveredAt).toLocaleDateString('sr-RS')} ({d.daysSince} dana)
            {d.notInDatabase ? ', nema je u povezanoj bazi' : ''}
          </li>
        ))}
        {backlog.count > backlog.documents.length && (
          <li>i jos {backlog.count - backlog.documents.length}…</li>
        )}
      </ul>
    </div>
  ) : null;

  if (!report.balances) {
    return (
      <>
        {staraGodina}
        {zaglavljene}
        <div className="health health--bad">
          <strong>Podaci se ne slažu sa ERP-om.</strong> ERP ima {report.erp.quantity} kom
          na {report.erp.articles} artikala, a lista prikazuje {report.reported.quantity} kom
          na {report.reported.articles}. Ne planirati dok se ovo ne razjasni.
        </div>
      </>
    );
  }

  const { resolved, needsReview, resolutionRate } = report.destinations;

  return (
    <>
      {staraGodina}
      {zaglavljene}
      <div className="health health--ok">
        Saldo se poklapa sa ERP-om: {report.erp.quantity} kom na {report.erp.articles} artikala.
        {needsReview > 0 && (
          <>
            {' '}Odredište razrešeno za {resolutionRate}% količine — <strong>{needsReview}</strong> stavki
            čeka tvoju potvrdu, {resolved} je spremno.
          </>
        )}
      </div>
    </>
  );
}
